"use client";

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import AlertDialog from "./AlertDialog";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    height: "32px",
    fontSize: "12px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
    "&:focus": {
      outline: "none",
      boxShadow: "none",
      border: "none",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "12px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "12px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: "0",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "2px",
  }),
};

export default function PODetailView() {
  const { id, poId } = useParams();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [po, setPO] = useState(null);
  const [editing, setEditing] = useState(false);
  const [initialItemsCount, setInitialItemsCount] = useState(0);
  const [vendors, setVendors] = useState([]);
  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const projectResponse = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const projectData = projectResponse.data;
        setProject(projectData);

        const poData = projectData.purchaseOrders.find(
          (p) => (p._id?.$oid || p._id) === poId,
        );
        if (!poData) throw new Error("Purchase Order not found");

        // Normalize _id fields to strings
        const normalizedPOData = {
          ...poData,
          _id: poData._id?.$oid || poData._id,
          costCode: poData.costCode?.$oid || poData.costCode,
          project: poData.project?.$oid || poData.project,
          createdBy: poData.createdBy?.$oid || poData.createdBy,
          company: poData.company?.$oid || poData.company,
          lineItems: poData.lineItems.map((item) => ({
            ...item,
            _id: item._id?.$oid || item._id,
            budgetItem: item.budgetItem?.$oid || item.budgetItem,
            projectItem: item.projectItem?.$oid || item.projectItem,
            vendor: item.vendor?.$oid || item.vendor,
            costCode: item.costCode?.$oid || item.costCode,
          })),
        };
        setPO(normalizedPOData);
        setInitialItemsCount(normalizedPOData.lineItems.length);

        const vendorsResponse = await axios.get(
          "https://erp-production-72da01c8e651.herokuapp.com/api/accounts/vendors",
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setVendors(vendorsResponse.data);
      } catch (err) {
        console.error(`Error fetching data: ${err.message}`);
        setAlert({
          open: true,
          type: "error",
          title: "Data Load Error",
          message:
            "Failed to load data: " +
            (err.response?.data?.message || err.message),
        });
      }
    };
    fetchData();
  }, [id, poId]);

  const handleItemSelect = (selectedOptions) => {
    const selectedIds = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];

    const newItems = availableItems
      .filter((item) => {
        const itemId = item._id?.$oid || item._id;
        return selectedIds.includes(itemId);
      })
      .map((item) => {
        const itemId = item._id?.$oid || item._id;
        return {
          _id: `temp-${Math.random().toString(36).substr(2, 9)}`,
          budgetItem: itemId,
          title: item.itemDescription || item.costCodeDescription || "",
          costCode: po.costCode?.$oid || po.costCode,
          quantity: item.requiredQty || item.quantity || 1,
          unitCost: item.cost || 0, // cost only — never price
          vendor: item.vendor || null,
          itemCode: item.itemCode || "",
          itemDescription: item.itemDescription || "",
          size: item.size || "",
          requiredQty: item.requiredQty || item.quantity || 0,
          container: item.container || "",
          bidArea: item.bidArea || "",
          uom: item.uom || "",
          laborHours: item.laborHours || 0,
        };
      });

    setPO((prev) => ({
      ...prev,
      lineItems: [
        ...prev.lineItems,
        ...newItems.filter(
          (newItem) =>
            !prev.lineItems.some((li) => li.budgetItem === newItem.budgetItem),
        ),
      ],
    }));
  };

  const handleRemoveItem = (itemId) => {
    console.log(`Attempting to remove item with ID: ${itemId}`);
    console.log(
      `Current lineItems: ${JSON.stringify(
        po.lineItems.map((item) => ({
          _id: item._id,
          budgetItem: item.budgetItem,
          projectItem: item.projectItem,
        })),
      )}`,
    );
    setPO((prev) => {
      const updatedLineItems = prev.lineItems.filter((item) => {
        const itemIdToCompare = item._id?.$oid || item._id;
        console.log(
          `Comparing item._id: ${itemIdToCompare} with itemId: ${itemId}`,
        );
        return itemIdToCompare !== itemId;
      });
      console.log(
        `Updated lineItems after removal: ${JSON.stringify(
          updatedLineItems.map((item) => ({
            _id: item._id,
            budgetItem: item.budgetItem,
            projectItem: item.projectItem,
          })),
        )}`,
      );
      return {
        ...prev,
        lineItems: updatedLineItems,
      };
    });
  };

  const handleSavePO = useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");

      // Validate negative quantities locally
      for (const item of po.lineItems) {
        if ((item.quantity || 0) < 0) {
          const matchingItem = po.lineItems.find(
            (li) =>
              li.itemCode === (item.itemCode || "") &&
              li.size === (item.size || "") &&
              li.bidArea === (item.bidArea || "") &&
              (li._id?.$oid || li._id) !== (item._id?.$oid || item._id),
          );
          if (!matchingItem) {
            throw new Error(
              `No existing item found for itemCode: ${
                item.itemCode || "N/A"
              }, size: ${item.size || "N/A"}, bidArea: ${
                item.bidArea || "N/A"
              } to decrement quantity`,
            );
          }
          if (matchingItem.quantity + (item.quantity || 0) < 0) {
            throw new Error(
              `Cannot reduce quantity below 0 for itemCode: ${
                item.itemCode || "N/A"
              }, size: ${item.size || "N/A"}, bidArea: ${item.bidArea || "N/A"}`,
            );
          }
        }
      }

      // Normalize lineItems to ensure _id, budgetItem, projectItem, etc. are strings
      const normalizedLineItems = po.lineItems.map((item) => {
        const isTemp =
          typeof item._id === "string" && item._id.startsWith("temp-");
        return {
          ...item,
          _id: isTemp ? undefined : item._id?.$oid || item._id,
          budgetItem: item.budgetItem?.$oid || item.budgetItem,
          projectItem: item.projectItem?.$oid || item.projectItem,
          vendor: item.vendor?.$oid || item.vendor,
          costCode: item.costCode?.$oid || item.costCode,
        };
      });

      const updatedPOData = {
        title: po.title,
        type: po.type,
        costCode: po.costCode?.$oid || po.costCode,
        estimatedCost: po.estimatedCost,
        lineItems: normalizedLineItems,
      };

      const response = await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/purchaseOrders/${poId}`,
        updatedPOData,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const updatedPO = response.data.data;

      // Normalize response data
      const normalizedUpdatedPO = {
        ...updatedPO,
        _id: updatedPO._id?.$oid || updatedPO._id,
        costCode: updatedPO.costCode?.$oid || updatedPO.costCode,
        project: updatedPO.project?.$oid || updatedPO.project,
        createdBy: updatedPO.createdBy?.$oid || updatedPO.createdBy,
        company: updatedPO.company?.$oid || updatedPO.company,
        lineItems: updatedPO.lineItems.map((item) => ({
          ...item,
          _id: item._id?.$oid || item._id,
          budgetItem: item.budgetItem?.$oid || item.budgetItem,
          projectItem: item.projectItem?.$oid || item.projectItem,
          vendor: item.vendor?.$oid || item.vendor,
          costCode: item.costCode?.$oid || item.costCode,
        })),
      };

      setPO(normalizedUpdatedPO);
      setProject((prev) => ({
        ...prev,
        purchaseOrders: prev.purchaseOrders.map((p) =>
          (p._id?.$oid || p._id) === poId ? normalizedUpdatedPO : p,
        ),
      }));

      setEditing(false);
      setInitialItemsCount(normalizedUpdatedPO.lineItems.length);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Purchase Order updated successfully",
      });
    } catch (err) {
      console.error(err.response?.data || err);
      let errorMessage = err.response?.data?.details || err.message;
      // Customize error message for negative quantity issues
      if (
        errorMessage.includes("No existing line item found") ||
        errorMessage.includes("Cannot decrement")
      ) {
        errorMessage =
          "Cannot process negative quantity: " +
          errorMessage +
          ". Please ensure the item exists in the PO.";
      }
      setAlert({
        open: true,
        type: "error",
        title: "Save Error",
        message: "Failed to update PO: " + errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  }, [po, poId]);

  const handleTitleChange = (e) => {
    setPO((prev) => ({
      ...prev,
      title: e.target.value,
    }));
  };

  const availableItems = (project?.budgets || [])
    .flatMap((budget) =>
      (budget.lineItems || [])
        .filter(
          (item) =>
            item.category === "M" && !item.linkedPO && !budget.isVariance,
        )
        .map((item) => ({
          ...item,
          _id: item._id?.$oid || item._id,
          source: "budget",
          budgetTitle: budget.title,
          isChangeOrder: budget.isChangeOrder || false,
          vendor:
            item.vendor && typeof item.vendor === "object"
              ? item.vendor._id?.$oid || item.vendor._id
              : item.vendor || null,
        })),
    )
    .filter((budgetItem) => {
      const budgetItemId = budgetItem._id?.$oid || budgetItem._id;
      const isInCurrentPO = po?.lineItems?.some(
        (poItem) =>
          (poItem.budgetItem?.$oid || poItem.budgetItem) === budgetItemId,
      );
      return !isInCurrentPO;
    });

  const availableItemOptions = availableItems.map((item) => ({
    value: item._id?.$oid || item._id,
    label: `${item.budgetTitle}${item.isChangeOrder ? " (CO)" : ""}: ${
      item.itemDescription || item.costCodeDescription || "Unnamed"
    } (Qty: ${item.requiredQty || item.quantity || 1}, Size: ${
      item.size || "N/A"
    }, Bid Area: ${item.bidArea || "N/A"}, Vendor: ${
      vendors.find((v) => (v._id?.$oid || v._id) === item.vendor)
        ?.companyName || "N/A"
    })`,
  }));

  const vendorOptions = vendors.map((vendor) => ({
    value: vendor._id?.$oid || vendor._id,
    label: vendor.companyName || "Unnamed Vendor",
  }));

  if (!project || !po)
    return <div className="text-center py-4 text-gray-200">Loading...</div>;

  const hasChanges =
    po.lineItems.length !== initialItemsCount ||
    po.title !==
      project.purchaseOrders.find((p) => (p._id?.$oid || p._id) === poId)
        ?.title;

  return (
    <div className="max-w-full mx-auto">
      <AlertDialog
        type={alert.type}
        title={alert.title}
        message={alert.message}
        open={alert.open}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={() => setAlert({ ...alert, open: false })}
        confirmText="OK"
        cancelText="Close"
      />
      <div className="mb-6 shadow-xl bg-gray-700 rounded-lg p-6 border border-gray-600">
        <div className="flex justify-between items-center">
          {editing ? (
            <input
              type="text"
              value={po.title}
              onChange={handleTitleChange}
              className="text-2xl font-semibold text-gray-200 bg-gray-600 border-gray-500 rounded-md p-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Enter PO title"
            />
          ) : (
            <h1 className="text-2xl font-semibold text-white">
              {po.title} (PO# {po.poNumber})
            </h1>
          )}
          <button
            onClick={() =>
              navigate(`/Projects/${id}`, {
                state: { initialTab: "Purchase Orders" },
              })
            }
            className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
          >
            Back
          </button>
        </div>
      </div>

      <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-white">
            Purchase Order Items
          </h2>
          <div className="flex space-x-4">
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
              >
                Edit PO
              </button>
            )}
            {editing && (
              <>
                <button
                  onClick={handleSavePO}
                  disabled={isSaving || !hasChanges}
                  className={`px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200 ${
                    isSaving || !hasChanges
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {isSaving ? "Saving..." : "Save PO"}
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    const originalPO = project.purchaseOrders.find(
                      (p) => (p._id?.$oid || p._id) === poId,
                    );
                    const normalizedOriginalPO = {
                      ...originalPO,
                      _id: originalPO._id?.$oid || originalPO._id,
                      costCode:
                        originalPO.costCode?.$oid || originalPO.costCode,
                      project: originalPO.project?.$oid || originalPO.project,
                      createdBy:
                        originalPO.createdBy?.$oid || originalPO.createdBy,
                      company: originalPO.company?.$oid || originalPO.company,
                      lineItems: originalPO.lineItems.map((item) => ({
                        ...item,
                        _id: item._id?.$oid || item._id,
                        budgetItem: item.budgetItem?.$oid || item.budgetItem,
                        projectItem: item.projectItem?.$oid || item.projectItem,
                        vendor: item.vendor?.$oid || item.vendor,
                        costCode: item.costCode?.$oid || item.costCode,
                      })),
                    };
                    setPO(normalizedOriginalPO);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {editing && (
          <div className="mb-4">
            <Select
              options={availableItemOptions}
              isMulti
              onChange={handleItemSelect}
              placeholder="Select items to add..."
              className="w-full"
              styles={customStyles}
              value={availableItemOptions.filter((opt) =>
                po.lineItems.some((li) => li.budgetItem === opt.value),
              )}
            />
          </div>
        )}

        <div className="rounded-lg shadow-xl">
          <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tl-lg">
                  Title
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Quantity
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Unit Cost
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-64">
                  Vendor
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-64">
                  Bid Area
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Container
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Total Cost
                </th>
                {editing ? (
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tr-lg">
                    Actions
                  </th>
                ) : (
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tr-lg"></th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-600">
              {po.lineItems.map((item, index) => (
                <tr
                  key={item._id || index}
                  className="hover:bg-gray-600 transition-colors duration-200"
                >
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {item.title}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {item.quantity}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    ${item.unitCost.toFixed(2)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200 w-64">
                    {vendors.find(
                      (v) =>
                        (v._id?.$oid || v._id) ===
                        (item.vendor?.$oid || item.vendor),
                    )?.companyName || "Unassigned"}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200 w-64">
                    {item.bidArea || "N/A"}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {item.size || "N/A"}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    {item.container || "N/A"}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                    ${(item.unitCost * item.quantity).toFixed(2)}
                  </td>
                  {editing && (
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                      <button
                        onClick={() =>
                          handleRemoveItem(item._id?.$oid || item._id)
                        }
                        className="text-orange-400 hover:text-orange-300 transition-colors duration-200"
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {po.lineItems.length === 0 && (
                <tr>
                  <td
                    colSpan={editing ? 9 : 8}
                    className="px-4 py-4 text-center text-sm text-gray-400"
                  >
                    No line items found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
